import {
  ErrorFetchingData,
  NoDataFound,
} from "@/components/common/user-messages";
import { useCallback, useEffect, useState } from "react";

import { DateRange } from "react-day-picker";
import { InteractionSummary } from "@/lib/messaging";
import { InteractionsTable } from "@/components/pages/interactions/interactions-table";
import { SearchSelectGroup } from "@/components/common/search-select-group";
import { useInteractionsList } from "@/hooks/api/useInteractionsApi";
import { useLearnLanguage } from "@/context/learn-language-context";
import { useTranslation } from "react-i18next";

export function InteractionsPage() {
  const { t } = useTranslation(["pages"]);
  const { languageProficiency } = useLearnLanguage();

  const [period, setPeriod] = useState("all");
  const [selectedMode, setSelectedMode] = useState("all"); // New state for mode
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [noData, setNoData] = useState(false);

  const [page, setPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [infiniteScrollEnabled, setInfiniteScrollEnabled] = useState(true);

  const [interactionData, setInteractionData] = useState<InteractionSummary[]>(
    [],
  );

  const ITEMS_PER_PAGE = 25; // Define how many records per page
  const {
    data: fetchedData,
    isLoading,
    error,
  } = useInteractionsList(
    page,
    ITEMS_PER_PAGE,
    period,
    languageProficiency?.language.key || "",
    selectedMode,
  );

  useEffect(() => {
    // Reset page and data when period, language, or mode changes
    setPage(1);
    setHasMoreData(true);
    setInfiniteScrollEnabled(true);
  }, [period, languageProficiency?.language.key, selectedMode]);

  const increasePage = useCallback(() => {
    if (!infiniteScrollEnabled) return;
    setPage((prevPage) => prevPage + 1);
  }, [infiniteScrollEnabled]);

  // Update noData and hasMoreData based on interactionData
  useEffect(() => {
    if (isLoading) {
      setNoData(false);
      return;
    }
    if (!fetchedData?.data || fetchedData?.data.length === 0) {
      setNoData(true);
      setInteractionData([]);
      setHasMoreData(false);
      setInfiniteScrollEnabled(false);
    } else {
      if (page === 1) {
        setInteractionData(fetchedData.data);
      } else {
        // Append new data to existing data
        setInteractionData((prevData) => [...prevData, ...fetchedData.data]);
      }
      setNoData(false);
      setHasMoreData(page < fetchedData.pages);
      setInfiniteScrollEnabled(true);
    }
  }, [fetchedData, page]);

  if (error) {
    return <ErrorFetchingData />;
  }

  return (
    <div className="min-h-screen bg-background p-4 mt-12 md:p-10 lg:p-12">
      <div className="mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-row gap-4 items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold tracking-tight">
              {t("pages:interactions.header")}
            </h1>
            <p className="hidden md:inline text-muted-foreground">
              {t("pages:interactions.subheader")}
            </p>
          </div>
          <SearchSelectGroup
            setPeriod={setPeriod}
            setMode={setSelectedMode}
            setDate={setDateRange}
          />
        </div>
        {noData && <NoDataFound />}
      </div>

      <div className="container mx-auto">
        <InteractionsTable
          data={interactionData}
          loading={isLoading}
          hasMore={hasMoreData}
          error={error}
          onIntersect={increasePage}
        />
      </div>
    </div>
  );
}
